/**
 * AdminLayout — persistent left-rail nav shell for all admin routes.
 *
 * Per docs/architecture/cfo365-redesign.md §2 (supersedes app-design.md §2.2).
 *
 * Three destinations, each answering one question:
 *   Today    — what came in, and where did it go?  (the money dashboard; its Ledger
 *              and Billing drill-downs stay under /money)
 *   Projects — how is the work going?      (portfolio + workforce + calendar + customers + approvals)
 *   More     — everything else             (utilities, account, settings)
 *
 * Founder, 2026-09-10: "Money is Today. 1st in line". The separate Money item is
 * gone, the old attention-queue Today (pages/Today.js) is no longer routed, and
 * Approvals moved under Projects. The mobile tabs made the same change that day.
 *
 * Settings is reached from More, not the rail — the brief calls for removing
 * duplicate navigation signals, and a rail item plus a hub entry is exactly that.
 *
 * Structure:
 *   - 240px rail (expanded) / 64px rail (collapsed)
 *   - White surface, border-r border-slate-200
 *   - Active item: bg-primary-soft + border-l-2 border-primary
 *   - Top bar: sticky; hamburger (<768px) + theme toggle, no breadcrumb
 *   - Bottom of rail: user avatar + name + role + sign out
 *   - <768px: hamburger opens a slide-in sheet drawer
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useSegment from '../hooks/useSegment';
import ThemeToggle from './ThemeToggle';
import DashboardBackdrop from './DashboardBackdrop';
// The 192px mark, not the 1024px app icon in assets/logo.png: this renders at
// 32px in the app shell of every admin page, and 113KB to draw 32 pixels is a
// real wait on a job-site connection. Same image, 7.6KB.
import logo from '../assets/logo-mark.png';
// The brand lockup (founder's public/Logo.png, cropped to the artwork and sized
// for the rail: 208KB → 26KB). White on transparent, which is why it belongs on
// the rail's black and nowhere light without a dark backing.
import logoLockup from '../assets/logo-lockup.png';
import '../workspace.css';

const { resolveJobLabel } = require('../services/segmentService');

// ─── Nav item definitions ────────────────────────────────────────────────────

// The Projects item label is segment-driven (resolveJobLabel) — built per-render
// in buildNavItems so contractor tenants see "Projects" and others see "Jobs".
// The ROUTE is /projects for every tenant; only the label varies.
function buildNavItems(jobLabel) {
  return [
    // Today is the money dashboard; its Ledger and Billing drill-downs live under
    // /money, so Today stays lit there too.
    { label: 'Today', icon: LayoutDashboard, to: '/today', alsoActiveUnder: '/money' },
    { label: `${jobLabel}s`, icon: Briefcase, to: '/projects' },
    // No More item (founder, 2026-09-17: "remove more", "not behind more").
    // The page held exactly one destination — Company settings — so a whole rail
    // item existed to hide one link. Settings now lives in the profile menu,
    // where people look for it.
  ];
}

// ─── Avatar initials helper ──────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getRoleLabel(role) {
  const map = { admin: 'Admin', 'super-admin': 'Super Admin', user: 'User', scanner: 'Scanner' };
  return map[role] || role;
}

// ─── Single nav link item ────────────────────────────────────────────────────

function NavItem({ item, collapsed, onClick }) {
  const { icon: Icon, label, to, alsoActiveUnder } = item;
  const { pathname } = useLocation();
  const activeBelow = Boolean(alsoActiveUnder)
    && (pathname === alsoActiveUnder || pathname.startsWith(`${alsoActiveUnder}/`));

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 rounded-r-md text-sm font-medium transition-colors duration-150',
          'cfo-nav-item group relative',
          collapsed ? 'justify-center px-0 py-2.5 mx-2' : 'px-3 py-2.5 mx-2',
          isActive || activeBelow
            ? 'bg-primary-soft text-primary-soft-text font-semibold border-l-2 border-primary -ml-px pl-[11px]'
            : 'text-text-muted hover:bg-surface-sunken hover:text-text',
        ].join(' ')
      }
      title={collapsed ? label : undefined}
    >
      {({ isActive }) => (
        <>
          <Icon
            className={['h-[18px] w-[18px] flex-shrink-0', isActive || activeBelow ? 'text-primary' : 'text-text-disabled group-hover:text-text-muted'].join(' ')}
            aria-hidden="true"
          />
          {!collapsed && <span className="truncate">{label}</span>}
          {/* Tooltip when collapsed */}
          {collapsed && (
            <span className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-text px-2 py-1 text-xs text-text-on-primary opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
              {label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

// ─── Rail component (shared between desktop sidebar and mobile drawer) ───────

function Rail({ collapsed, onToggleCollapse, onClose, isMobile }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const jobLabel = resolveJobLabel(useSegment()); // 'Project' | 'Job'
  const navItems = buildNavItems(jobLabel);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // The profile menu (founder, 2026-09-17: "when you click the profile name bob
  // it opens a modal and settings is on the list item"). Closes on Escape and on
  // any click outside — a menu you can only dismiss by finding the same small
  // target again is worse than no menu.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    const onPointerDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [userMenuOpen]);

  const initials = getInitials(user?.name || user?.displayName || user?.email || '');
  const displayName = user?.name || user?.displayName || user?.email || 'Admin';
  const roleLabel = getRoleLabel(user?.role);

  // No overflow-hidden on the rail: things anchored inside it have to escape it.
  // The collapsed rail is 64px, so the profile menu and the nav tooltips — both
  // wider than that by necessity — were clipped away entirely, which made the
  // collapsed menu unusable and its tooltips invisible.
  return (
    <div className="cfo-rail relative flex flex-col h-full bg-surface border-r border-border">
      {/* The logo is centred in the rail (founder, 2026-09-14). The collapse and
          close controls are taken OUT of the flow and pinned right, because a
          button sharing the row would push the logo off-centre by half its
          width — centred against the leftover space, not against the rail. */}
      <div
        className={[
          'relative flex items-center justify-center flex-shrink-0 h-14 border-b border-border',
          collapsed && !isMobile ? 'gap-1 px-2' : 'px-4',
        ].join(' ')}
      >
        {(!collapsed || isMobile) && (
          // The lockup IS the name — no text beside it, or the brand reads
          // twice. It carries its own alt text for the same reason.
          <img src={logoLockup} alt="CFO365.ai" className="cfo-lockup" />
        )}
        {collapsed && !isMobile && (
          // 64px of rail cannot hold a wordmark legibly, so the square mark
          // stands in until the rail is expanded again.
          <img src={logo} alt="CFO365.ai" className="cfo-mark" />
        )}
        {/* Mobile: close button */}
        {isMobile && (
          <button
            onClick={onClose}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-7 w-7 rounded-md text-text-disabled hover:bg-surface-sunken hover:text-text-muted transition-colors duration-150"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* The collapse handle lives on the rail's edge at mid-height, not in the
          header (founder, 2026-09-14: "move the arrow to a different spot").
          Sharing a row with a centred logo made it read as part of the brand;
          on the seam it reads as what it is — the thing that moves the wall.
          Mid-height also keeps it clear of the nav items, which stack from the
          top, and of the user footer at the bottom. */}
      {!isMobile && (
        <button
          onClick={onToggleCollapse}
          className="absolute right-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-white/5 opacity-70 transition-opacity duration-150 hover:opacity-100"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      )}

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5" aria-label="Main navigation">
        {navItems.map((item) => (
          <NavItem key={item.to} item={item} collapsed={collapsed && !isMobile} onClick={isMobile ? onClose : undefined} />
        ))}
      </nav>

      {/* User footer — the identity IS the menu trigger. Sign out moved inside
          it: a destructive action sitting permanently in the rail is one stray
          click from ending someone's session. */}
      <div ref={userMenuRef} className="relative flex-shrink-0 border-t border-border p-3">
        {userMenuOpen && (
          <div
            role="menu"
            aria-label="Account"
            // Fixed width, not inset-from-both-edges: on the 64px collapsed rail
            // that would leave ~40px and clip "Settings" to a letter.
            className="absolute bottom-full left-3 z-50 mb-2 w-48 overflow-hidden rounded-xl border border-border bg-surface shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => { setUserMenuOpen(false); if (isMobile && onClose) onClose(); navigate('/settings'); }}
              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-text hover:bg-surface-sunken transition-colors duration-150"
            >
              <Settings className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>Settings</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setUserMenuOpen(false); handleSignOut(); }}
              className="flex w-full items-center gap-2.5 border-t border-border px-3 py-2.5 text-left text-sm text-text hover:bg-surface-sunken transition-colors duration-150"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setUserMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          title={collapsed && !isMobile ? displayName : undefined}
          className={[
            'flex w-full items-center rounded-md transition-colors duration-150 hover:bg-surface-sunken',
            collapsed && !isMobile ? 'justify-center p-1' : 'gap-2.5 p-1.5',
          ].join(' ')}
        >
          <span className="h-8 w-8 rounded-full bg-surface-sunken flex items-center justify-center text-xs font-semibold text-text-muted flex-shrink-0">
            {initials}
          </span>
          {(!collapsed || isMobile) && (
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-sm font-semibold text-text truncate">{displayName}</span>
              <span className="block text-xs text-text-muted truncate">{roleLabel}</span>
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── AdminLayout (root export) ───────────────────────────────────────────────

export default function AdminLayout() {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Only admin role gets the full nav rail. user/scanner roles that reach
  // shared routes (jobs/:jobId, finance/invoices/:invoiceId) just get the content.
  const showRail = user && (user.role === 'admin' || user.role === 'employer' || user.role === 'recruiter');

  // Close mobile drawer on resize to desktop
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e) => { if (e.matches) setMobileOpen(false); };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Prevent scroll on body when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleMenuOpen = useCallback(() => setMobileOpen(true), []);
  const handleMobileClose = useCallback(() => setMobileOpen(false), []);
  const handleToggleCollapse = useCallback(() => setCollapsed((c) => !c), []);

  if (!showRail) {
    // Non-admin users accessing shared routes — render content only, no chrome.
    // Floating toggle keeps light/dark reachable without a top bar.
    return (
      <div className="min-h-screen bg-bg">
        <ThemeToggle floating />
        <Outlet />
      </div>
    );
  }

  return (
    // No bg-bg on the shell: an opaque ground here would paint straight over the
    // backdrop. The backdrop's own gradient is the floor now.
    <div className="cfo-shell flex h-screen overflow-hidden">
      <DashboardBackdrop />
      {/* ── Desktop left rail ────────────────────────────────────────────── */}
      <aside
        className={[
          'hidden md:flex flex-col flex-shrink-0 transition-[width] duration-200 ease-out',
          collapsed ? 'w-16' : 'w-60',
        ].join(' ')}
        aria-label="Admin navigation"
      >
        <Rail
          collapsed={collapsed}
          onToggleCollapse={handleToggleCollapse}
          isMobile={false}
        />
      </aside>

      {/* ── Mobile overlay + drawer ───────────────────────────────────────── */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-md md:hidden"
            onClick={handleMobileClose}
            aria-hidden="true"
          />
          {/* Drawer */}
          <aside
            className="fixed inset-y-0 left-0 z-40 w-60 flex flex-col md:hidden"
            aria-label="Admin navigation"
          >
            <Rail
              collapsed={false}
              onToggleCollapse={null}
              onClose={handleMobileClose}
              isMobile={true}
            />
          </aside>
        </>
      )}

      {/* ── Main content area ─────────────────────────────────────────────── */}
      {/* No top bar (founder, 2026-09-14). It had already lost its breadcrumb and
          was carrying nothing but a theme toggle across the full width — a strip
          of chrome charging rent for one button. Its two real jobs moved to
          floating controls, so the content now starts at the top of the shell. */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <main className="cfo-main flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* The menu button is the ONLY way to reach navigation below 768px, where
          the rail is a drawer. It cannot go with the bar that used to hold it.

          Hidden while the drawer is open: it shares the drawer's layer and is
          painted after it, so it would otherwise sit on top of the drawer's own
          logo and close button. Once the drawer is open it has nothing to do. */}
      {!mobileOpen && (
      <button
        onClick={handleMenuOpen}
        aria-label="Open navigation"
        title="Open navigation"
        className="md:hidden fixed top-4 left-4 z-40 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-text-muted shadow-md transition-colors duration-150 hover:text-text"
      >
        <Menu className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>
      )}

      <ThemeToggle floating />
    </div>
  );
}
